import { type FC, useContext, useState } from "react";

import { DraftTemplateRoleEnum } from "@app_schema";

import { DOCX_EXTENSION, DOCX_MIME_TYPE } from "@application/utilities/file";

import { FileContext } from "@styled/file_context";
import { FileManager } from "@styled/file_manager";
import { FilePicker } from "@styled/file_picker";
import { FileQueue } from "@styled/file_queue";
import { UploadChoice } from "@styled/upload_choice";

import { renderFileQueueItem } from "./shared_card_utils";

const ACCEPT = [DOCX_EXTENSION, DOCX_MIME_TYPE].join(",");

const ROLES = [
  { value: DraftTemplateRoleEnum.CaseOnly, label: "This case only" },
  {
    value: DraftTemplateRoleEnum.Template,
    label: "My template",
    hint: "new cases start from it",
    note: "Your sections don't carry over. Add them again with Insert here.",
  },
];

// The picker holds the file he picks until he says what it's for; Upload then
// sends it as the manager's own pick.
const HeldPicker: FC<{
  role: DraftTemplateRoleEnum;
  onRole(role: DraftTemplateRoleEnum): void;
}> = ({ role, onRole }) => {
  const manager = useContext(FileContext);
  const [held, setHeld] = useState<File | null>(null);

  if (held) {
    return (
      <UploadChoice
        fileName={held.name}
        options={ROLES}
        value={role}
        onChange={onRole}
        onCancel={() => setHeld(null)}
        onUpload={() => {
          manager.onSelect([held]);
          setHeld(null);
        }}
      />
    );
  }

  return (
    <FileContext.Provider
      value={{ ...manager, onSelect: (files) => setHeld(files[0] ?? null) }}
    >
      <FilePicker
        title="Upload a Word draft"
        subtitle="For this case only, or as your template."
        action="Upload draft"
        accept={ACCEPT}
        single
      />
    </FileContext.Provider>
  );
};

// A Word draft for a case when he already has a template: once he picks it,
// he says whether it's for this case only (the default) or his new template.
export const DraftingUploadChoice: FC<{
  save(blob: { signed_id: string }, role: DraftTemplateRoleEnum): Promise<void>;
}> = ({ save }) => {
  const [role, setRole] = useState(DraftTemplateRoleEnum.CaseOnly);

  return (
    <FileManager save={(blob) => save(blob, role)}>
      <HeldPicker role={role} onRole={setRole} />
      <FileQueue render={renderFileQueueItem} />
    </FileManager>
  );
};
